import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Volume2, Shield, AlertTriangle, Heart, CheckCircle, Eye, Users, Baby, Brain, Utensils, Home, FileText, Activity } from 'lucide-react';

// Activité 2 - Risques et prévention
// Identification des risques lors de l'accompagnement de l'enfant et mise en œuvre d'actions de prévention adéquates

// Quiz final - 20 questions
const QUIZ_ACTIVITE2 = [
  // PARTIE 1 - QCM (1 seule réponse)
  {
    id: 1,
    type: 'single',
    question: "La première cause d'accident chez le jeune enfant est :",
    options: ["Les intoxications", "Les chutes", "Les brûlures", "Les morsures"],
    correct: 1,
    explanation: "Les chutes sont la première cause d'accident chez le jeune enfant, notamment depuis la table à langer, les escaliers ou lors de l'apprentissage de la marche."
  },
  {
    id: 2,
    type: 'single',
    question: "Sur une table à langer, l'AP doit :",
    options: ["Se retourner rapidement", "Laisser l'enfant si elle reste proche", "Garder toujours une main sur l'enfant", "Installer un jouet pour le distraire"],
    correct: 2,
    explanation: "L'AP doit TOUJOURS garder une main sur l'enfant sur la table à langer pour prévenir les chutes."
  },
  {
    id: 3,
    type: 'single',
    question: "Une morsure entre enfants est généralement liée à :",
    options: ["Une volonté de faire mal", "Une immaturité émotionnelle", "Un trouble psychiatrique", "Une provocation volontaire"],
    correct: 1,
    explanation: "Avant 3 ans, les morsures sont liées à l'immaturité émotionnelle et à la difficulté d'exprimer frustration, colère ou fatigue."
  },
  {
    id: 4,
    type: 'single',
    question: "Une « douce violence » est :",
    options: ["Une sanction éducative", "Un acte involontaire mais inadapté", "Une maltraitance grave", "Une règle de sécurité"],
    correct: 1,
    explanation: "Les douces violences sont des actes involontaires mais inadaptés qui peuvent impacter l'enfant (forcer à manger, changer sans prévenir...)."
  },
  {
    id: 5,
    type: 'single',
    question: "En cas de suspicion de maltraitance, l'AP doit :",
    options: ["Interroger l'enfant seule", "Observer et transmettre", "Accuser les parents", "Ignorer si elle doute"],
    correct: 1,
    explanation: "L'AP doit observer, consigner des faits objectifs et transmettre à l'équipe. Elle ne doit jamais interroger, accuser ou enquêter."
  },
  {
    id: 6,
    type: 'single',
    question: "Une fausse route est favorisée par :",
    options: ["Une posture assise calme", "Une surveillance active", "Un enfant qui mange en courant", "Des aliments adaptés"],
    correct: 2,
    explanation: "Manger en marchant ou en jouant augmente le risque de fausse route. L'enfant doit être assis calmement et surveillé."
  },
  {
    id: 7,
    type: 'single',
    question: "L'intervention précoce permet :",
    options: ["D'éviter toute difficulté", "D'améliorer l'évolution de l'enfant", "De remplacer le médecin", "D'éviter le travail d'équipe"],
    correct: 1,
    explanation: "L'intervention précoce permet d'améliorer l'évolution de l'enfant en cas de retard de développement ou de vulnérabilité."
  },
  {
    id: 8,
    type: 'single',
    question: "Une vulnérabilité peut être liée à :",
    options: ["La précarité", "Le nombre de jouets", "L'âge de l'AP", "Le planning"],
    correct: 0,
    explanation: "La précarité (financière, logement, isolement) est une source de vulnérabilité qui peut impacter l'enfant et sa famille."
  },
  {
    id: 9,
    type: 'single',
    question: "L'AP ne doit jamais :",
    options: ["Observer", "Adapter l'environnement", "Poser un diagnostic médical", "Transmettre"],
    correct: 2,
    explanation: "L'AP observe et transmet mais ne pose JAMAIS de diagnostic médical. C'est le rôle du médecin."
  },
  {
    id: 10,
    type: 'single',
    question: "Une brûlure peut être causée par :",
    options: ["Une eau de bain trop chaude", "Un tapis", "Un livre", "Une chaise"],
    correct: 0,
    explanation: "L'eau du bain trop chaude (>37°C) peut causer des brûlures. L'AP doit toujours vérifier la température."
  },
  // PARTIE 2 - Vrai/Faux
  {
    id: 11,
    type: 'truefalse',
    question: "Une noyade peut survenir dans très peu d'eau.",
    options: ["Vrai", "Faux"],
    correct: 0,
    explanation: "VRAI - Une noyade peut survenir dans seulement quelques centimètres d'eau (baignoire, seau, bassine)."
  },
  {
    id: 12,
    type: 'truefalse',
    question: "Forcer un enfant à manger peut être une douce violence.",
    options: ["Vrai", "Faux"],
    correct: 0,
    explanation: "VRAI - Forcer à manger ou finir l'assiette est une douce violence qui peut créer des troubles alimentaires."
  },
  {
    id: 13,
    type: 'truefalse',
    question: "L'AP peut décider seule d'un signalement officiel.",
    options: ["Vrai", "Faux"],
    correct: 1,
    explanation: "FAUX - L'AP transmet à l'équipe et à la hiérarchie. Le signalement officiel suit un protocole impliquant la direction."
  },
  {
    id: 14,
    type: 'truefalse',
    question: "Une morsure est toujours volontaire.",
    options: ["Vrai", "Faux"],
    correct: 1,
    explanation: "FAUX - Chez le jeune enfant, la morsure est souvent une pulsion non intentionnelle liée à l'immaturité émotionnelle."
  },
  {
    id: 15,
    type: 'truefalse',
    question: "La prévention consiste à anticiper les risques.",
    options: ["Vrai", "Faux"],
    correct: 0,
    explanation: "VRAI - Prévenir c'est anticiper les risques avant qu'ils ne surviennent (aménagement, surveillance, protocoles)."
  },
  // PARTIE 3 - QCM multiples
  {
    id: 16,
    type: 'multiple',
    question: "Parmi les actions suivantes, lesquelles préviennent les chutes ?",
    options: ["Ranger les espaces de circulation", "Laisser l'enfant seul sur la table à langer", "Installer des tapis amortissants", "Adapter le matériel à l'âge"],
    correct: [0, 2, 3],
    explanation: "Ranger, installer des tapis amortissants et adapter le matériel préviennent les chutes. Ne JAMAIS laisser l'enfant seul sur la table à langer."
  },
  {
    id: 17,
    type: 'multiple',
    question: "Les signes pouvant évoquer un retard de développement sont :",
    options: ["Peu d'interactions", "Difficulté à marcher après 20 mois", "Colère occasionnelle", "Peu de mots à 2 ans"],
    correct: [0, 1, 3],
    explanation: "Peu d'interactions, marche tardive et peu de mots à 2 ans sont des signes d'alerte. La colère occasionnelle est normale."
  },
  {
    id: 18,
    type: 'multiple',
    question: "Une douce violence peut être :",
    options: ["Parler de l'enfant à la 3e personne devant lui", "Forcer à finir son assiette", "Verbaliser le soin", "Changer l'enfant sans prévenir"],
    correct: [0, 1, 3],
    explanation: "Parler de l'enfant devant lui, forcer à manger et changer sans prévenir sont des douces violences. Verbaliser le soin est une bonne pratique."
  },
  {
    id: 19,
    type: 'multiple',
    question: "Face à un enfant qui mord, l'AP doit :",
    options: ["Humilier l'enfant devant les autres", "Nommer l'émotion", "Proposer une alternative", "Exclure systématiquement"],
    correct: [1, 2],
    explanation: "L'AP doit nommer l'émotion et proposer une alternative. Humilier ou exclure systématiquement est inapproprié."
  },
  {
    id: 20,
    type: 'multiple',
    question: "Le rôle de l'AP dans la prévention est de :",
    options: ["Observer", "Adapter l'environnement", "Poser un diagnostic", "Transmettre les informations"],
    correct: [0, 1, 3],
    explanation: "L'AP observe, adapte l'environnement et transmet. Elle ne pose JAMAIS de diagnostic médical."
  }
];

const SLIDES = [
  // SLIDE 1 - Titre
  {
    id: 1,
    type: 'title',
    title: "Activité 2",
    subtitle: "Identifier les risques chez l'enfant et mettre en œuvre des actions de prévention",
    icon: Shield,
    color: "#EF4444",
    narration: "Bienvenue dans l'Activité 2. Cette activité porte sur l'identification des risques chez l'enfant et la mise en œuvre d'actions de prévention."
  },
  // SLIDE 2 - Sommaire
  {
    id: 2,
    type: 'table_of_contents',
    title: "Sommaire de la Formation",
    color: "#EF4444",
    sections: [
      { title: "1. Notion de risque & prévention", slides: "3-4", targetSlide: 3, color: "#EF4444", icon: "Shield" },
      { title: "2. Rôle de l'AP", slides: "5-6", targetSlide: 5, color: "#3B82F6", icon: "Eye" },
      { title: "3. Risques physiques", slides: "7-12", targetSlide: 7, color: "#F59E0B", icon: "AlertTriangle" },
      { title: "4. Risques relationnels", slides: "13-16", targetSlide: 13, color: "#EC4899", icon: "Heart" },
      { title: "5. Risques psycho-sociaux", slides: "17-19", targetSlide: 17, color: "#8B5CF6", icon: "Users" },
      { title: "6. Santé & développement", slides: "20-22", targetSlide: 20, color: "#10B981", icon: "Activity" },
      { title: "7. Risques sanitaires", slides: "23-25", targetSlide: 23, color: "#0EA5E9", icon: "Shield" },
      { title: "8. Posture professionnelle", slides: "26-27", targetSlide: 26, color: "#06B6D4", icon: "FileText" },
      { title: "9. Conclusion", slides: "28", targetSlide: 28, color: "#A855F7", icon: "CheckCircle" },
      { title: "10. Quiz final", slides: "29", targetSlide: 29, color: "#22C55E", icon: "CheckCircle" }
    ],
    narration: "Voici le sommaire de cette formation sur la prévention des risques. Cliquez sur une section pour y accéder directement."
  },
  // SLIDE 3 - Section 1: Notion de risque
  {
    id: 3,
    type: 'section',
    title: "Comprendre la notion de risque",
    subtitle: "Définition, vulnérabilité de l'enfant et niveaux de prévention",
    icon: Shield,
    color: "#EF4444",
    gradient: "linear-gradient(135deg, #EF4444 0%, #F87171 50%, #FCA5A5 100%)",
    image: "https://images.unsplash.com/photo-1632052999485-d748103abf98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxjaGlsZGNhcmUlMjBwcm9mZXNzaW9uYWwlMjBudXJzZSUyMGJhYnklMjBjYXJlZ2l2ZXJ8ZW58MHx8fHwxNzcxODc0NDE3fDA&ixlib=rb-4.1.0&q=85",
    narration: "Commençons par comprendre la notion de risque et pourquoi l'enfant est particulièrement vulnérable."
  },
  // SLIDE 4 - Contenu notion de risque
  {
    id: 4,
    type: 'risk_definition',
    title: "Comprendre la notion de risque",
    color: "#EF4444",
    definition: {
      title: "Définition",
      text: "Situation pouvant entraîner un dommage (physique, psychique, relationnel).",
      note: "Le risque augmente quand l'environnement n'est pas adapté ou la supervision insuffisante."
    },
    vulnerability: {
      title: "Pourquoi l'enfant est vulnérable ?",
      points: [
        "Immaturité motrice et cognitive",
        "Exploration / imitation",
        "Difficulté à exprimer douleur, peur, besoins"
      ]
    },
    prevention: {
      title: "Prévention : 3 niveaux",
      levels: [
        { icon: "🔮", text: "Prévenir (anticiper)" },
        { icon: "🛡️", text: "Protéger (sécuriser)" },
        { icon: "🚨", text: "Réagir (secours, alerte, traçabilité)" }
      ]
    },
    narration: "Le risque est une situation pouvant entraîner un dommage. L'enfant est vulnérable en raison de son immaturité. La prévention passe par 3 niveaux : prévenir, protéger et réagir."
  },
  // SLIDE 5 - Section 2: Rôle de l'AP
  {
    id: 5,
    type: 'section',
    title: "Le rôle central de l'AP",
    subtitle: "Observer • Prévenir • Protéger",
    icon: Eye,
    color: "#3B82F6",
    gradient: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 50%, #93C5FD 100%)",
    image: "https://images.unsplash.com/photo-1737064731018-bac688187682?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjaGlsZGNhcmUlMjBwcm9mZXNzaW9uYWwlMjBudXJzZSUyMGJhYnklMjBjYXJlZ2l2ZXJ8ZW58MHx8fHwxNzcxODc0NDE3fDA&ixlib=rb-4.1.0&q=85",
    narration: "L'auxiliaire de puériculture joue un rôle central dans la prévention des risques."
  },
  // SLIDE 6 - Contenu rôle AP
  {
    id: 6,
    type: 'ap_role_risk',
    title: "Le rôle de l'AP : une action en continu",
    color: "#3B82F6",
    observer: {
      title: "Observer (repérage)",
      icon: "👁️",
      points: [
        "Surveiller l'environnement et le matériel (conformité, usure, obstacles)",
        "Observer l'enfant : comportement, motricité, douleur, fatigue, alimentation",
        "Repérer les interactions adulte–enfant (bientraitance)",
        "Noter des faits objectifs (date, contexte, signes observés)"
      ]
    },
    prevent: {
      title: "Prévenir / Protéger (actions)",
      icon: "🛡️",
      points: [
        "Adapter l'espace : zones de jeux, coins calmes, barrières, tapis",
        "Sécuriser les gestes de soin : table à langer, bain, portage",
        "Sensibiliser : règles simples et constantes, verbalisation",
        "Appliquer les protocoles : hygiène, sécurité, urgence, signalement"
      ]
    },
    transmit: {
      title: "Transmissions & traçabilité",
      icon: "📝",
      points: [
        "Transmettre à l'oral + écrire si nécessaire (fait, contexte, conduite tenue)",
        "Utiliser un vocabulaire neutre : décrire, éviter jugements/interprétations",
        "Alerter la hiérarchie selon gravité (IDE, EJE, direction)",
        "Informer les parents selon le cadre de la structure"
      ]
    },
    narration: "L'AP observe, prévient, protège et transmet. Son action est continue et s'inscrit dans le travail d'équipe."
  },
  // SLIDE 7 - Section 3: Risques physiques
  {
    id: 7,
    type: 'section',
    title: "Risques physiques",
    subtitle: "Chutes, brûlures, intoxications, fausses routes...",
    icon: AlertTriangle,
    color: "#F59E0B",
    gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #FCD34D 100%)",
    image: "https://images.unsplash.com/photo-1761208663763-c4d30657c910?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHx0b2RkbGVyJTIwcGxheWluZyUyMHRveXMlMjBkYXljYXJlJTIwZWR1Y2F0aW9ufGVufDB8fHx8MTc3MTg3NDQxN3ww&ixlib=rb-4.1.0&q=85",
    narration: "Passons aux risques physiques : les accidents les plus fréquents chez l'enfant."
  },
  // SLIDE 8 - Les chutes
  {
    id: 8,
    type: 'physical_risk',
    title: "Les chutes",
    color: "#F59E0B",
    icon: "🤕",
    situations: {
      title: "Situations à risque",
      points: [
        "Table à langer / plan en hauteur",
        "Escaliers, marchepieds, meubles à grimper",
        "Jeux inadaptés à l'âge, bousculades"
      ]
    },
    factors: {
      title: "Facteurs aggravants",
      points: [
        "Surveillance interrompue",
        "Espace encombré",
        "Chaussures/chaussettes glissantes"
      ]
    },
    prevention: {
      title: "Prévention (AP)",
      points: [
        "Main toujours sur l'enfant sur table à langer",
        "Rangement + circulation dégagée",
        "Tapis amortissants / barrières",
        "Accompagner l'apprentissage moteur (verbaliser, sécuriser)"
      ]
    },
    action: {
      title: "Conduite à tenir",
      points: [
        "Évaluer : conscience, vomissements, douleur, hématome",
        "Appliquer protocole, surveiller, informer, tracer"
      ]
    },
    narration: "Les chutes sont fréquentes. L'AP prévient en gardant toujours une main sur l'enfant et en sécurisant l'espace."
  },
  // SLIDE 9 - Les brûlures
  {
    id: 9,
    type: 'physical_risk',
    title: "Les brûlures",
    color: "#F59E0B",
    icon: "🔥",
    situations: {
      title: "Origines",
      points: [
        "Liquides chauds (biberons, cafés)",
        "Eau du bain trop chaude",
        "Radiateurs, plaques, fours",
        "Soleil (coups de soleil)"
      ]
    },
    prevention: {
      title: "Prévention (AP)",
      points: [
        "Vérifier températures (bain ~37°C, aliments)",
        "Éloigner boissons chaudes, sécuriser cuisine",
        "Protections radiateurs",
        "Prise en charge du soleil (ombre, vêtements, crème selon protocole)"
      ]
    },
    action: {
      title: "Conduite à tenir",
      points: [
        "Refroidir à l'eau tempérée (selon protocole), protéger, alerter",
        "Ne pas percer les cloques",
        "Tracer l'événement"
      ]
    },
    narration: "Les brûlures peuvent avoir plusieurs origines. La prévention passe par le contrôle des températures et la sécurisation de l'environnement."
  },
  // SLIDE 10 - Fausses routes
  {
    id: 10,
    type: 'physical_risk',
    title: "Fausses routes & étouffements",
    color: "#F59E0B",
    icon: "🍇",
    situations: {
      title: "Aliments à risque",
      points: [
        "Raisins entiers, tomates cerises, olives",
        "Saucisses en rondelles, cacahuètes, bonbons durs",
        "Morceaux trop gros / textures non adaptées"
      ]
    },
    factors: {
      title: "Situations à risque",
      points: [
        "Manger en marchant/jouant",
        "Repas en groupe sans supervision",
        "Mise à disposition de petits objets"
      ]
    },
    prevention: {
      title: "Prévention (AP)",
      points: [
        "Adapter textures / découper longitudinalement",
        "Posture assise et calme pendant le repas",
        "Surveillance active + rythme de l'enfant",
        "Vérifier l'environnement (petits objets)"
      ]
    },
    action: {
      title: "Conduite à tenir",
      points: [
        "Appliquer gestes de secours appris (structure)",
        "Alerter, tracer"
      ]
    },
    narration: "Les fausses routes sont dangereuses. L'AP adapte les textures, assure une posture assise calme et une surveillance active pendant les repas."
  },
  // SLIDE 11 - Intoxications
  {
    id: 11,
    type: 'physical_risk',
    title: "Intoxications & risques domestiques",
    color: "#F59E0B",
    icon: "☠️",
    situations: {
      title: "Sources fréquentes",
      points: [
        "Produits ménagers, capsules de lessive",
        "Médicaments",
        "Plantes toxiques",
        "Cosmétiques (gel hydroalcoolique, parfums…)"
      ]
    },
    prevention: {
      title: "Prévention (AP)",
      points: [
        "Stockage en hauteur / placards fermés",
        "Ne jamais transvaser en bouteille alimentaire",
        "Vérifier sacs/poches (parents, intervenants)",
        "Respecter protocole de rangement et d'entretien"
      ]
    },
    action: {
      title: "Conduite à tenir",
      points: [
        "Protocole + centre antipoison / SAMU selon consignes",
        "Conserver l'emballage/produit pour l'identification",
        "Tracer l'incident"
      ]
    },
    narration: "Les intoxications peuvent venir de nombreuses sources. Le stockage sécurisé et le respect des protocoles sont essentiels."
  },
  // SLIDE 12 - Autres risques physiques
  {
    id: 12,
    type: 'other_physical_risks',
    title: "Autres risques physiques",
    color: "#F59E0B",
    risks: [
      {
        title: "Coincements & étranglement",
        icon: "🔗",
        points: ["Cordons, colliers, attaches tétines inadaptées", "Barreaux, portes, tiroirs"]
      },
      {
        title: "Noyade (même faible hauteur d'eau)",
        icon: "💧",
        points: ["Baignoire, bassines, seaux, piscines", "Jamais de surveillance « à distance »"]
      },
      {
        title: "Électrisation",
        icon: "⚡",
        points: ["Prises non protégées, câbles abîmés"]
      }
    ],
    prevention: {
      title: "Prévention (AP)",
      points: [
        "Contrôle quotidien des espaces + matériel",
        "Règles claires en équipe, check-lists si besoin"
      ]
    },
    narration: "D'autres risques physiques incluent les coincements, la noyade et l'électrisation. Un contrôle quotidien est indispensable."
  },
  // SLIDE 13 - Section 4: Risques relationnels
  {
    id: 13,
    type: 'section',
    title: "Risques relationnels",
    subtitle: "Pulsions non intentionnelles • Douces violences • VEO",
    icon: Heart,
    color: "#EC4899",
    gradient: "linear-gradient(135deg, #EC4899 0%, #F472B6 50%, #FBCFE8 100%)",
    image: "https://images.pexels.com/photos/13692378/pexels-photo-13692378.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    narration: "Abordons maintenant les risques relationnels : pulsions, douces violences et violences éducatives ordinaires."
  },
  // SLIDE 14 - Pulsions non intentionnelles
  {
    id: 14,
    type: 'relational_risk',
    title: "Pulsions non intentionnelles",
    subtitle: "Tapes, griffures, morsures",
    color: "#EC4899",
    why: {
      title: "Pourquoi ?",
      points: [
        "Immaturité émotionnelle",
        "Frustration, fatigue, séparation",
        "Difficulté à verbaliser"
      ]
    },
    apRole: {
      title: "Ce que l'AP fait",
      points: [
        "Intervenir immédiatement, sécuriser",
        "Nommer l'émotion : « tu es en colère »",
        "Proposer une alternative : mordre un anneau, demander un tour",
        "Mettre des repères constants (règles simples)"
      ]
    },
    parents: {
      title: "Avec les parents",
      points: [
        "Informer de façon factuelle (sans désigner un « méchant »)",
        "Rappeler que c'est fréquent selon l'âge",
        "Proposer des pistes cohérentes avec l'équipe"
      ]
    },
    narration: "Les pulsions non intentionnelles sont normales chez l'enfant. L'AP intervient, nomme l'émotion et propose des alternatives."
  },
  // SLIDE 15 - Douces violences
  {
    id: 15,
    type: 'relational_risk',
    title: "Les « douces violences »",
    subtitle: "Involontaires mais impactantes",
    color: "#EC4899",
    examples: {
      title: "Exemples",
      points: [
        "Forcer à manger / à finir l'assiette",
        "Changer sans prévenir, gestes brusques",
        "Parler de l'enfant à la 3e personne devant lui",
        "Ignorer pleurs ou signaux"
      ]
    },
    impacts: {
      title: "Impacts possibles",
      points: [
        "Insécurité affective",
        "Perte de confiance",
        "Troubles alimentaires/du sommeil",
        "Relations dégradées"
      ]
    },
    prevention: {
      title: "Prévention (AP)",
      points: [
        "Verbaliser chaque soin, demander la participation",
        "Respecter le rythme, proposer plutôt qu'imposer",
        "Réfléchir en équipe (analyse de pratiques)",
        "S'auto-observer : stress, fatigue, surcharge"
      ]
    },
    narration: "Les douces violences sont involontaires mais impactantes. L'AP verbalise, respecte le rythme et s'auto-observe."
  },
  // SLIDE 16 - VEO
  {
    id: 16,
    type: 'relational_risk',
    title: "Violences éducatives ordinaires (VEO)",
    subtitle: "Gestes/paroles humiliants",
    color: "#EC4899",
    definition: {
      title: "Définition",
      text: "Gestes/paroles humiliants : gifles, menaces, chantage, cris…",
      note: "Ce n'est pas éducatif : peur ≠ apprentissage"
    },
    apRole: {
      title: "Ce que l'AP peut faire",
      points: [
        "Rester professionnelle, ne pas juger",
        "Relayer vers l'équipe/hiérarchie",
        "Proposer des alternatives (cadre, limites, routines)",
        "Orienter vers ressources (PMI, parentalité) selon procédure"
      ]
    },
    narration: "Les violences éducatives ordinaires ne sont pas éducatives. L'AP reste professionnelle et relaye vers l'équipe."
  },
  // SLIDE 17 - Section 5: Risques psycho-sociaux
  {
    id: 17,
    type: 'section',
    title: "Risques psycho-sociaux",
    subtitle: "Maltraitance • Négligence • Vulnérabilités",
    icon: Users,
    color: "#8B5CF6",
    gradient: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 50%, #C4B5FD 100%)",
    image: "https://images.pexels.com/photos/3662631/pexels-photo-3662631.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    narration: "Passons aux risques psycho-sociaux : maltraitance, négligence et vulnérabilités."
  },
  // SLIDE 18 - Maltraitance & négligence
  {
    id: 18,
    type: 'psychosocial_risk',
    title: "Maltraitance & négligence : repérer et agir",
    color: "#8B5CF6",
    signals: {
      title: "Signaux d'alerte (exemples)",
      points: [
        "Hématomes répétitifs ou localisations atypiques",
        "Retrait, hypervigilance, peur de l'adulte",
        "Retard de soins, hygiène très insuffisante",
        "Discours parental incohérent / minimisation"
      ]
    },
    apDoes: {
      title: "Ce que l'AP fait (cadre)",
      points: [
        "Observer et consigner des faits",
        "Transmettre à l'équipe (IDE, direction…)",
        "Suivre le protocole interne (information préoccupante / signalement)"
      ]
    },
    apDoesNot: {
      title: "Ce que l'AP ne fait pas",
      points: [
        "Interroger l'enfant de façon insistante",
        "Accuser, enquêter, promettre le secret"
      ]
    },
    narration: "Face à la maltraitance, l'AP observe, consigne et transmet. Elle ne doit pas interroger ni enquêter."
  },
  // SLIDE 19 - Vulnérabilités
  {
    id: 19,
    type: 'psychosocial_risk',
    title: "Vulnérabilités",
    subtitle: "Précarité, handicap, isolement",
    color: "#8B5CF6",
    examples: {
      title: "Exemples",
      points: [
        "Difficultés financières / logement",
        "Handicap ou maladie chronique",
        "Isolement, barrière linguistique",
        "Parents épuisés, situation migratoire…"
      ]
    },
    risks: {
      title: "Risques associés",
      points: [
        "Accès aux soins réduit",
        "Stress familial, ruptures de suivi",
        "Retards de développement non repérés"
      ]
    },
    apRole: {
      title: "Rôle de l'AP",
      points: [
        "Accueillir sans stigmatiser",
        "Adapter l'accompagnement (inclusion)",
        "Travailler en réseau (PMI, CAMSP, CMP, associations) via l'équipe"
      ]
    },
    narration: "Face aux vulnérabilités, l'AP accueille sans stigmatiser, adapte son accompagnement et travaille en réseau."
  },
  // SLIDE 20 - Section 6: Santé & développement
  {
    id: 20,
    type: 'section',
    title: "Santé & développement",
    subtitle: "Malnutrition • Retards de développement",
    icon: Activity,
    color: "#10B981",
    gradient: "linear-gradient(135deg, #10B981 0%, #34D399 50%, #6EE7B7 100%)",
    image: "https://images.unsplash.com/photo-1763218812866-a237afef2cc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx0b2RkbGVyJTIwcGxheWluZyUyMHRveXMlMjBkYXljYXJlJTIwZWR1Y2F0aW9ufGVufDB8fHx8MTc3MTg3NDQxN3ww&ixlib=rb-4.1.0&q=85",
    narration: "Abordons les risques liés à la santé et au développement de l'enfant."
  },
  // SLIDE 21 - Malnutrition
  {
    id: 21,
    type: 'health_risk',
    title: "Malnutrition : surpoids/obésité et dénutrition",
    color: "#10B981",
    surpoids: {
      title: "Surpoids / obésité",
      icon: "📈",
      factors: "Grignotage, boissons sucrées, manque d'activité",
      reperes: "Suivi courbes de croissance (par l'équipe médicale)",
      apRole: "Proposer des routines actives, jeux moteurs, hydratation",
      note: "Éviter jugement : accompagner, informer avec tact"
    },
    denutrition: {
      title: "Dénutrition",
      icon: "📉",
      factors: "Sélectivité alimentaire, pathologie, précarité",
      signes: "Fatigue, perte de poids, retard staturo-pondéral",
      apRole: "Observer quantités, textures, rythme",
      note: "Travailler avec PAI / IDE / médecin selon structure"
    },
    narration: "La malnutrition peut être surpoids ou dénutrition. L'AP propose des routines actives et observe les habitudes alimentaires."
  },
  // SLIDE 22 - Retards de développement
  {
    id: 22,
    type: 'development_risk',
    title: "Retards de développement : repères et observation",
    color: "#10B981",
    domains: [
      {
        title: "Moteur",
        icon: "🏃",
        signs: ["Marche tardive", "Chutes très fréquentes", "Rigidité/hypotonie"]
      },
      {
        title: "Langagier",
        icon: "💬",
        signs: ["Peu de mots à 2 ans", "Ne suit pas consignes simples", "Frustration"]
      },
      {
        title: "Psychique / relationnel",
        icon: "🧠",
        signs: ["Peu d'interactions", "Évite le regard", "Jeux répétitifs"]
      },
      {
        title: "Sensoriel",
        icon: "👁️",
        signs: ["Réagit peu aux sons", "Évite certaines textures", "Hypersensibilités"]
      }
    ],
    apRole: {
      title: "Rôle AP",
      points: [
        "Observer dans le quotidien, noter objectivement",
        "Partager à l'équipe, proposer activités adaptées",
        "Rassurer : « on va en parler avec l'équipe » (sans diagnostic)"
      ]
    },
    narration: "L'AP observe les signes de retard de développement et partage ses observations avec l'équipe, sans poser de diagnostic."
  },
  // SLIDE 23 - Section: Risques sanitaires
  {
    id: 23,
    type: 'section',
    title: "Risques sanitaires",
    subtitle: "Prévention des infections • Vaccinations",
    icon: Shield,
    color: "#0EA5E9",
    gradient: "linear-gradient(135deg, #0EA5E9 0%, #38BDF8 50%, #7DD3FC 100%)",
    image: "https://images.unsplash.com/photo-1632997771590-9328df0a8a0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjaGlsZGNhcmUlMjBwcm9mZXNzaW9uYWwlMjBudXJzZSUyMGJhYnklMjBjYXJlZ2l2ZXJ8ZW58MHx8fHwxNzcxODc0NDE3fDA&ixlib=rb-4.1.0&q=85",
    narration: "Abordons les risques sanitaires : prévention des infections et vaccinations."
  },
  // SLIDE 24 - Vaccinations
  {
    id: 24,
    type: 'vaccinations',
    title: "Vaccinations obligatoires pour les nourrissons",
    color: "#0EA5E9",
    vaccines: [
      { name: "BCG (Tuberculose)", ages: "1 mois", color: "#A855F7" },
      { name: "DTP + Coqueluche", ages: "2, 4, 11 mois + rappels", color: "#EF4444" },
      { name: "Haemophilus (Hib)", ages: "2, 4, 11 mois", color: "#22C55E" },
      { name: "Hépatite B", ages: "2, 4, 11 mois", color: "#F59E0B" },
      { name: "Pneumocoque", ages: "2, 4, 11 mois", color: "#EC4899" },
      { name: "ROR (Rougeole-Oreillons-Rubéole)", ages: "12, 16-18 mois", color: "#F97316" },
      { name: "Méningocoques ACWY", ages: "6, 12 mois", color: "#14B8A6" },
      { name: "Méningocoque B", ages: "3, 5, 12 mois", color: "#8B5CF6" }
    ],
    apRole: {
      title: "Rôle de l'AP",
      points: [
        "Vérifier que le carnet de santé est à jour (selon procédure)",
        "Respecter les protocoles en cas de maladie contagieuse",
        "Participer à l'éducation à la santé auprès des familles (sans jugement)"
      ]
    },
    calendarImage: "https://customer-assets.emergentagent.com/job_8d1b7ffb-82e3-4293-a07b-f258e760c4ca/artifacts/y8qspzyt_image.png",
    narration: "Les vaccinations sont obligatoires pour protéger les nourrissons. L'AP vérifie le carnet de santé et participe à l'éducation à la santé."
  },
  // SLIDE 25 - Les 3 niveaux de prévention
  {
    id: 25,
    type: 'prevention_levels',
    title: "Les 3 niveaux de prévention",
    subtitle: "Rappel théorique pour l'examen",
    color: "#0EA5E9",
    levels: [
      {
        number: "1",
        title: "Prévention primaire",
        description: "Éviter la maladie",
        examples: ["Vaccins", "Hygiène des mains", "Aération des locaux", "Alimentation équilibrée"],
        icon: "🔮",
        color: "#22C55E"
      },
      {
        number: "2",
        title: "Prévention secondaire",
        description: "Dépister précocement",
        examples: ["Bilans de santé", "Surveillance du développement", "Repérage des signes d'alerte"],
        icon: "🔍",
        color: "#F59E0B"
      },
      {
        number: "3",
        title: "Prévention tertiaire",
        description: "Éviter les complications",
        examples: ["Suivi médical", "Rééducation", "PAI", "Coordination des soins"],
        icon: "🏥",
        color: "#3B82F6"
      }
    ],
    narration: "Il existe 3 niveaux de prévention : primaire pour éviter la maladie, secondaire pour dépister, tertiaire pour éviter les complications."
  },
  // SLIDE 26 - Section 7: Posture professionnelle
  {
    id: 26,
    type: 'section',
    title: "Posture professionnelle & outils",
    subtitle: "Bientraitance • Analyse de pratiques • Protocoles • Communication",
    icon: FileText,
    color: "#06B6D4",
    gradient: "linear-gradient(135deg, #06B6D4 0%, #22D3EE 50%, #67E8F9 100%)",
    image: "https://images.unsplash.com/photo-1632997771590-9328df0a8a0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjaGlsZGNhcmUlMjBwcm9mZXNzaW9uYWwlMjBudXJzZSUyMGJhYnklMjBjYXJlZ2l2ZXJ8ZW58MHx8fHwxNzcxODc0NDE3fDA&ixlib=rb-4.1.0&q=85",
    narration: "Terminons avec la posture professionnelle et les outils concrets pour l'AP."
  },
  // SLIDE 27 - Outils concrets
  {
    id: 27,
    type: 'professional_tools',
    title: "Outils concrets pour l'AP",
    color: "#06B6D4",
    tools: [
      {
        title: "Aménagement",
        icon: "🏠",
        points: ["Zonage, repères visuels, rangement accessible", "Matériel adapté à l'âge et contrôlé"]
      },
      {
        title: "Routines",
        icon: "🔄",
        points: ["Repas, sieste, soins : repères stables → sécurité affective"]
      },
      {
        title: "Communication",
        icon: "💬",
        points: ["Verbalisation des gestes, écoute, messages clairs", "Co-éducation avec les parents, cohérence d'équipe"]
      },
      {
        title: "Éthique",
        icon: "⭐",
        points: ["Respect du rythme, consentement recherché", "Refus des violences, vigilance aux « douces violences »"]
      },
      {
        title: "Équipe",
        icon: "👥",
        points: ["Transmissions structurées, réunions, analyse de situations"]
      }
    ],
    examples: {
      title: "Exemples d'observations professionnelles",
      cases: [
        { situation: "Chutes", action: "enfant en apprentissage de la marche → sécurisation de l'espace + verbalisation après la chute" },
        { situation: "Morsures", action: "tensions liées au partage → intervention, mots sur l'émotion, médiation, information aux parents" },
        { situation: "Douces violences", action: "moment de change pressé → prise de conscience, ralentir, expliquer le soin" },
        { situation: "Alimentation", action: "sélectivité ou refus → observation des textures, proposer sans forcer, transmissions" }
      ]
    },
    narration: "L'AP dispose de nombreux outils : aménagement, routines, communication, éthique et travail d'équipe."
  },
  // SLIDE 28 - Conclusion
  {
    id: 28,
    type: 'final_conclusion',
    title: "Conclusion",
    color: "#A855F7",
    gradient: "linear-gradient(135deg, #A855F7 0%, #C084FC 50%, #E9D5FF 100%)",
    summary: [
      "Prévenir = sécuriser sans empêcher l'autonomie",
      "L'AP protège par l'observation, l'adaptation de l'environnement et la bientraitance",
      "Travailler en équipe et respecter les protocoles : clé de la sécurité de l'enfant"
    ],
    icon: CheckCircle,
    narration: "En conclusion : prévenir c'est sécuriser sans empêcher l'autonomie. L'AP protège par l'observation et la bientraitance. Le travail d'équipe est la clé de la sécurité."
  },
  // SLIDE 29 - Quiz final
  {
    id: 29,
    type: 'quiz_activite2',
    title: "Quiz final - 20 questions",
    color: "#22C55E",
    narration: "Place au quiz final de 20 questions pour valider vos connaissances sur la prévention des risques."
  }
];

const DEAPActivite2Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    selectedAnswers: [],
    showResult: false,
    score: 0,
    completed: false
  });

  const slide = SLIDES[currentSlide];

  const goToSlide = (slideIndex) => {
    setCurrentSlide(Math.max(0, Math.min(SLIDES.length - 1, slideIndex - 1)));
  };

  const handlePrevious = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentSlide((prev) => Math.min(SLIDES.length - 1, prev + 1));
  };

  const handleSpeak = async () => {
    if (!slide.narration) return;
    setIsPlaying(true);
    
    // Transformer le texte pour une meilleure lecture TTS
    const transformTextForTTS = (text) => {
      return text
        .replace(/\bAP\b/g, "auxiliaire de puériculture")
        .replace(/\bL'AP\b/g, "L'auxiliaire de puériculture")
        .replace(/\bVEO\b/g, "violences éducatives ordinaires")
        .replace(/\bPMI\b/g, "protection maternelle et infantile")
        .replace(/\bIDE\b/g, "infirmière")
        .replace(/\bEJE\b/g, "éducatrice de jeunes enfants");
    };
    
    try {
      const response = await fetch(`${window.location.origin}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: transformTextForTTS(slide.narration), voice: 'nova' })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.audio_base64) {
          // Convert base64 to audio
          const audioSrc = `data:audio/mp3;base64,${data.audio_base64}`;
          const audio = new Audio(audioSrc);
          audio.onended = () => setIsPlaying(false);
          audio.onerror = () => setIsPlaying(false);
          audio.play();
        } else {
          setIsPlaying(false);
        }
      } else {
        console.error('TTS response not ok');
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
    }
  };

  // Quiz functions
  const handleSelectAnswer = (answerIndex) => {
    const currentQ = QUIZ_ACTIVITE2[quizState.currentQuestion];
    if (quizState.showResult) return;
    
    if (currentQ.type === 'multiple') {
      // Pour les QCM multiples, toggle la sélection
      setQuizState(prev => {
        const newSelected = prev.selectedAnswers.includes(answerIndex)
          ? prev.selectedAnswers.filter(i => i !== answerIndex)
          : [...prev.selectedAnswers, answerIndex];
        return { ...prev, selectedAnswers: newSelected };
      });
    } else {
      // Pour les QCM simples et vrai/faux
      setQuizState(prev => ({ ...prev, selectedAnswers: [answerIndex] }));
    }
  };

  const handleValidateQuizAnswer = () => {
    const currentQ = QUIZ_ACTIVITE2[quizState.currentQuestion];
    let isCorrect = false;
    
    if (currentQ.type === 'multiple') {
      // Pour QCM multiples, vérifier que toutes les bonnes réponses sont sélectionnées
      const correctSet = new Set(currentQ.correct);
      const selectedSet = new Set(quizState.selectedAnswers);
      isCorrect = correctSet.size === selectedSet.size && 
                  [...correctSet].every(x => selectedSet.has(x));
    } else {
      isCorrect = quizState.selectedAnswers[0] === currentQ.correct;
    }
    
    setQuizState(prev => ({
      ...prev,
      showResult: true,
      score: isCorrect ? prev.score + 1 : prev.score
    }));
  };

  const handleNextQuizQuestion = () => {
    if (quizState.currentQuestion < QUIZ_ACTIVITE2.length - 1) {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        selectedAnswers: [],
        showResult: false
      }));
    } else {
      setQuizState(prev => ({ ...prev, completed: true }));
    }
  };

  const resetQuiz = () => {
    setQuizState({
      currentQuestion: 0,
      selectedAnswers: [],
      showResult: false,
      score: 0,
      completed: false
    });
  };

  const renderSlideContent = () => {
    switch (slide.type) {
      case 'title':
        const TitleIcon = slide.icon;
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <TitleIcon size={60} style={{ color: slide.color }} />
            </div>
            <h1 style={{ color: slide.color, fontSize: '3.5rem', marginBottom: '1rem', fontWeight: 'bold' }}>{slide.title}</h1>
            <p style={{ color: '#444', fontSize: '1.5rem', maxWidth: '700px', margin: '0 auto 1rem' }}>{slide.subtitle}</p>
            {slide.description && <p style={{ color: '#888', fontSize: '1.1rem' }}>{slide.description}</p>}
          </div>
        );

      case 'table_of_contents':
        return (
          <div>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>Cliquez sur une section pour y accéder directement</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
              {slide.sections.map((section, index) => (
                <div
                  key={index}
                  onClick={() => goToSlide(section.targetSlide)}
                  style={{
                    padding: '1rem',
                    background: `${section.color}10`,
                    borderRadius: '0.75rem',
                    border: `2px solid ${section.color}30`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: section.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.85rem'
                    }}>{index + 1}</div>
                    <h3 style={{ color: section.color, margin: 0, fontSize: '0.95rem' }}>{section.title}</h3>
                  </div>
                  <p style={{ color: '#888', fontSize: '0.8rem', margin: 0, marginLeft: '36px' }}>Slides {section.slides}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        const SectionIcon = slide.icon;
        return (
          <div style={{ 
            textAlign: 'center',
            padding: '2rem',
            borderRadius: '1.5rem',
            background: slide.gradient || `linear-gradient(135deg, ${slide.color} 0%, ${slide.color}90 100%)`,
            color: 'white',
            margin: '-1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '2rem'
          }}>
            {slide.image && (
              <div style={{
                width: '200px',
                height: '200px',
                borderRadius: '1rem',
                overflow: 'hidden',
                flexShrink: 0,
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
              }}>
                <img src={slide.image} alt={slide.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: slide.image ? '0' : '0 auto 1.5rem'
              }}>
                <SectionIcon size={40} style={{ color: 'white' }} />
              </div>
              <h2 style={{ fontSize: '2.5rem', marginBottom: '0.75rem', color: 'white', marginTop: '1rem' }}>{slide.title}</h2>
              <p style={{ fontSize: '1.3rem', opacity: 0.9, color: 'white' }}>{slide.subtitle}</p>
            </div>
          </div>
        );

      case 'risk_definition':
        return (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '1.5rem', fontSize: '2rem' }}>{slide.title}</h2>
            
            {/* Définition */}
            <div style={{ background: `${slide.color}10`, padding: '1.25rem', borderRadius: '1rem', marginBottom: '1rem', borderLeft: `4px solid ${slide.color}` }}>
              <h3 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.definition.title}</h3>
              <p style={{ color: '#333', marginBottom: '0.5rem' }}>{slide.definition.text}</p>
              <p style={{ color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>{slide.definition.note}</p>
            </div>
            
            {/* Vulnérabilité */}
            <div style={{ background: '#FEF3C7', padding: '1.25rem', borderRadius: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ color: '#B45309', marginBottom: '0.75rem' }}>{slide.vulnerability.title}</h3>
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {slide.vulnerability.points.map((point, i) => (
                  <li key={i} style={{ color: '#78350F', marginBottom: '0.25rem' }}>{point}</li>
                ))}
              </ul>
            </div>
            
            {/* Prévention */}
            <div style={{ background: '#D1FAE5', padding: '1.25rem', borderRadius: '1rem' }}>
              <h3 style={{ color: '#047857', marginBottom: '0.75rem' }}>{slide.prevention.title}</h3>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {slide.prevention.levels.map((level, i) => (
                  <div key={i} style={{ 
                    background: 'white', 
                    padding: '0.75rem 1rem', 
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flex: '1',
                    minWidth: '150px'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>{level.icon}</span>
                    <span style={{ color: '#047857' }}>{level.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'ap_role_risk':
        return (
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '1.5rem', fontSize: '2rem' }}>{slide.title}</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
              {/* Observer */}
              <div style={{ background: '#EFF6FF', padding: '1.25rem', borderRadius: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{slide.observer.icon}</span>
                  <h3 style={{ color: '#1D4ED8', margin: 0 }}>{slide.observer.title}</h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
                  {slide.observer.points.map((point, i) => (
                    <li key={i} style={{ color: '#1E40AF', marginBottom: '0.35rem' }}>{point}</li>
                  ))}
                </ul>
              </div>
              
              {/* Prévenir */}
              <div style={{ background: '#F0FDF4', padding: '1.25rem', borderRadius: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{slide.prevent.icon}</span>
                  <h3 style={{ color: '#16A34A', margin: 0 }}>{slide.prevent.title}</h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
                  {slide.prevent.points.map((point, i) => (
                    <li key={i} style={{ color: '#166534', marginBottom: '0.35rem' }}>{point}</li>
                  ))}
                </ul>
              </div>
              
              {/* Transmettre */}
              <div style={{ background: '#FEF9C3', padding: '1.25rem', borderRadius: '1rem', gridColumn: 'span 2' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{slide.transmit.icon}</span>
                  <h3 style={{ color: '#A16207', margin: 0 }}>{slide.transmit.title}</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                  {slide.transmit.points.map((point, i) => (
                    <div key={i} style={{ 
                      background: 'white', 
                      padding: '0.5rem 0.75rem', 
                      borderRadius: '0.5rem',
                      color: '#78350F',
                      fontSize: '0.85rem'
                    }}>{point}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'physical_risk':
        return (
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <span style={{ fontSize: '2.5rem' }}>{slide.icon}</span>
              <h2 style={{ color: slide.color, margin: 0, fontSize: '2rem' }}>{slide.title}</h2>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {/* Situations */}
              <div style={{ background: '#FEF2F2', padding: '1rem', borderRadius: '0.75rem' }}>
                <h4 style={{ color: '#B91C1C', marginBottom: '0.5rem', fontSize: '0.95rem' }}>{slide.situations.title}</h4>
                <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.85rem' }}>
                  {slide.situations.points.map((point, i) => (
                    <li key={i} style={{ color: '#991B1B', marginBottom: '0.25rem' }}>{point}</li>
                  ))}
                </ul>
              </div>
              
              {/* Facteurs si présent */}
              {slide.factors && (
                <div style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#B45309', marginBottom: '0.5rem', fontSize: '0.95rem' }}>{slide.factors.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.85rem' }}>
                    {slide.factors.points.map((point, i) => (
                      <li key={i} style={{ color: '#92400E', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Prévention */}
              <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem' }}>
                <h4 style={{ color: '#047857', marginBottom: '0.5rem', fontSize: '0.95rem' }}>{slide.prevention.title}</h4>
                <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.85rem' }}>
                  {slide.prevention.points.map((point, i) => (
                    <li key={i} style={{ color: '#065F46', marginBottom: '0.25rem' }}>{point}</li>
                  ))}
                </ul>
              </div>
              
              {/* Conduite */}
              <div style={{ background: '#EFF6FF', padding: '1rem', borderRadius: '0.75rem' }}>
                <h4 style={{ color: '#1D4ED8', marginBottom: '0.5rem', fontSize: '0.95rem' }}>{slide.action.title}</h4>
                <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.85rem' }}>
                  {slide.action.points.map((point, i) => (
                    <li key={i} style={{ color: '#1E40AF', marginBottom: '0.25rem' }}>{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'other_physical_risks':
        return (
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '1.5rem', fontSize: '2rem' }}>{slide.title}</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              {slide.risks.map((risk, i) => (
                <div key={i} style={{ background: `${slide.color}10`, padding: '1rem', borderRadius: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>{risk.icon}</span>
                    <h4 style={{ color: slide.color, margin: 0, fontSize: '0.9rem' }}>{risk.title}</h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.8rem' }}>
                    {risk.points.map((point, j) => (
                      <li key={j} style={{ color: '#444', marginBottom: '0.2rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem' }}>
              <h4 style={{ color: '#047857', marginBottom: '0.5rem' }}>{slide.prevention.title}</h4>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {slide.prevention.points.map((point, i) => (
                  <div key={i} style={{ background: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem', color: '#065F46', fontSize: '0.9rem' }}>
                    {point}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'relational_risk':
        return (
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '0.25rem', fontSize: '2rem' }}>{slide.title}</h2>
            {slide.subtitle && <p style={{ textAlign: 'center', color: '#666', marginBottom: '1.5rem', fontSize: '1.1rem' }}>{slide.subtitle}</p>}
            
            {/* Définition si présent */}
            {slide.definition && (
              <div style={{ background: `${slide.color}10`, padding: '1rem', borderRadius: '0.75rem', marginBottom: '1rem', textAlign: 'center' }}>
                <p style={{ color: '#333', marginBottom: '0.25rem' }}>{slide.definition.text}</p>
                <p style={{ color: slide.color, fontWeight: 'bold', margin: 0 }}>{slide.definition.note}</p>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              {/* Why */}
              {slide.why && (
                <div style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#B45309', marginBottom: '0.5rem' }}>{slide.why.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.why.points.map((point, i) => (
                      <li key={i} style={{ color: '#78350F', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Examples */}
              {slide.examples && (
                <div style={{ background: '#FEE2E2', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#B91C1C', marginBottom: '0.5rem' }}>{slide.examples.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.examples.points.map((point, i) => (
                      <li key={i} style={{ color: '#991B1B', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Impacts */}
              {slide.impacts && (
                <div style={{ background: '#FCE7F3', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#BE185D', marginBottom: '0.5rem' }}>{slide.impacts.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.impacts.points.map((point, i) => (
                      <li key={i} style={{ color: '#9D174D', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* AP Role */}
              {slide.apRole && (
                <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#047857', marginBottom: '0.5rem' }}>{slide.apRole.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.apRole.points.map((point, i) => (
                      <li key={i} style={{ color: '#065F46', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Parents */}
              {slide.parents && (
                <div style={{ background: '#EFF6FF', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#1D4ED8', marginBottom: '0.5rem' }}>{slide.parents.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.parents.points.map((point, i) => (
                      <li key={i} style={{ color: '#1E40AF', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Prévention */}
              {slide.prevention && (
                <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem', gridColumn: 'span 2' }}>
                  <h4 style={{ color: '#047857', marginBottom: '0.5rem' }}>{slide.prevention.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.prevention.points.map((point, i) => (
                      <li key={i} style={{ color: '#065F46', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'psychosocial_risk':
        return (
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '0.25rem', fontSize: '2rem' }}>{slide.title}</h2>
            {slide.subtitle && <p style={{ textAlign: 'center', color: '#666', marginBottom: '1.5rem', fontSize: '1.1rem' }}>{slide.subtitle}</p>}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              {/* Signals */}
              {slide.signals && (
                <div style={{ background: '#FEE2E2', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#B91C1C', marginBottom: '0.5rem' }}>{slide.signals.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.signals.points.map((point, i) => (
                      <li key={i} style={{ color: '#991B1B', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Examples */}
              {slide.examples && (
                <div style={{ background: `${slide.color}15`, padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.examples.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.examples.points.map((point, i) => (
                      <li key={i} style={{ color: '#444', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Risks */}
              {slide.risks && (
                <div style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#B45309', marginBottom: '0.5rem' }}>{slide.risks.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.risks.points.map((point, i) => (
                      <li key={i} style={{ color: '#78350F', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* AP Does */}
              {slide.apDoes && (
                <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#047857', marginBottom: '0.5rem' }}>{slide.apDoes.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.apDoes.points.map((point, i) => (
                      <li key={i} style={{ color: '#065F46', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* AP Does Not */}
              {slide.apDoesNot && (
                <div style={{ background: '#FEE2E2', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#B91C1C', marginBottom: '0.5rem' }}>{slide.apDoesNot.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.apDoesNot.points.map((point, i) => (
                      <li key={i} style={{ color: '#991B1B', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* AP Role */}
              {slide.apRole && (
                <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#047857', marginBottom: '0.5rem' }}>{slide.apRole.title}</h4>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {slide.apRole.points.map((point, i) => (
                      <li key={i} style={{ color: '#065F46', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'health_risk':
        return (
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '1.5rem', fontSize: '2rem' }}>{slide.title}</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              {/* Surpoids */}
              <div style={{ background: '#FEF3C7', padding: '1.25rem', borderRadius: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{slide.surpoids.icon}</span>
                  <h3 style={{ color: '#B45309', margin: 0 }}>{slide.surpoids.title}</h3>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#78350F', marginBottom: '0.5rem' }}><strong>Facteurs :</strong> {slide.surpoids.factors}</p>
                <p style={{ fontSize: '0.85rem', color: '#78350F', marginBottom: '0.5rem' }}><strong>Repères :</strong> {slide.surpoids.reperes}</p>
                <p style={{ fontSize: '0.85rem', color: '#047857', marginBottom: '0.5rem' }}><strong>AP :</strong> {slide.surpoids.apRole}</p>
                <p style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>{slide.surpoids.note}</p>
              </div>
              
              {/* Dénutrition */}
              <div style={{ background: '#DBEAFE', padding: '1.25rem', borderRadius: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{slide.denutrition.icon}</span>
                  <h3 style={{ color: '#1D4ED8', margin: 0 }}>{slide.denutrition.title}</h3>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#1E40AF', marginBottom: '0.5rem' }}><strong>Facteurs :</strong> {slide.denutrition.factors}</p>
                <p style={{ fontSize: '0.85rem', color: '#1E40AF', marginBottom: '0.5rem' }}><strong>Signes :</strong> {slide.denutrition.signes}</p>
                <p style={{ fontSize: '0.85rem', color: '#047857', marginBottom: '0.5rem' }}><strong>AP :</strong> {slide.denutrition.apRole}</p>
                <p style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>{slide.denutrition.note}</p>
              </div>
            </div>
          </div>
        );

      case 'development_risk':
        return (
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '1.5rem', fontSize: '2rem' }}>{slide.title}</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.domains.map((domain, i) => (
                <div key={i} style={{ background: `${slide.color}10`, padding: '1rem', borderRadius: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>{domain.icon}</span>
                    <h4 style={{ color: slide.color, margin: 0, fontSize: '0.9rem' }}>{domain.title}</h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.8rem' }}>
                    {domain.signs.map((sign, j) => (
                      <li key={j} style={{ color: '#444', marginBottom: '0.2rem' }}>{sign}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem' }}>
              <h4 style={{ color: '#047857', marginBottom: '0.5rem' }}>{slide.apRole.title}</h4>
              <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                {slide.apRole.points.map((point, i) => (
                  <li key={i} style={{ color: '#065F46', marginBottom: '0.25rem' }}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'vaccinations':
        return (
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.8rem' }}>{slide.title}</h2>
            
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              {/* Liste des vaccins */}
              <div style={{ flex: '1' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
                  {slide.vaccines.map((vaccine, i) => (
                    <div key={i} style={{ 
                      background: `${vaccine.color}15`, 
                      padding: '0.6rem 0.8rem', 
                      borderRadius: '0.5rem',
                      borderLeft: `3px solid ${vaccine.color}`
                    }}>
                      <p style={{ margin: 0, fontWeight: '600', color: vaccine.color, fontSize: '0.85rem' }}>{vaccine.name}</p>
                      <p style={{ margin: 0, color: '#666', fontSize: '0.75rem' }}>{vaccine.ages}</p>
                    </div>
                  ))}
                </div>
                
                {/* Rôle de l'AP */}
                <div style={{ background: '#D1FAE5', padding: '1rem', borderRadius: '0.75rem' }}>
                  <h4 style={{ color: '#047857', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>👩‍⚕️</span> {slide.apRole.title}
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem' }}>
                    {slide.apRole.points.map((point, i) => (
                      <li key={i} style={{ color: '#065F46', marginBottom: '0.25rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Image du calendrier vaccinal */}
              {slide.calendarImage && (
                <div style={{ 
                  width: '280px', 
                  flexShrink: 0,
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}>
                  <img 
                    src={slide.calendarImage} 
                    alt="Calendrier vaccinal" 
                    style={{ width: '100%', height: 'auto' }}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 'prevention_levels':
        return (
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '0.5rem', fontSize: '2rem' }}>{slide.title}</h2>
            <p style={{ textAlign: 'center', color: '#666', marginBottom: '1.5rem' }}>📚 {slide.subtitle}</p>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              {slide.levels.map((level, i) => (
                <div key={i} style={{ 
                  flex: 1,
                  background: `${level.color}10`,
                  borderRadius: '1rem',
                  padding: '1.25rem',
                  borderTop: `4px solid ${level.color}`
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem', 
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: level.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.25rem'
                    }}>
                      {level.number}
                    </div>
                    <span style={{ fontSize: '1.5rem' }}>{level.icon}</span>
                  </div>
                  
                  <h3 style={{ color: level.color, marginBottom: '0.25rem', fontSize: '1.1rem' }}>{level.title}</h3>
                  <p style={{ color: '#666', marginBottom: '0.75rem', fontWeight: '500', fontSize: '0.95rem' }}>{level.description}</p>
                  
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.85rem' }}>
                    {level.examples.map((example, j) => (
                      <li key={j} style={{ color: '#444', marginBottom: '0.2rem' }}>{example}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'professional_tools':
        return (
          <div style={{ maxWidth: '900px', margin: '0 auto', maxHeight: '500px', overflowY: 'auto' }}>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '1rem', fontSize: '2rem' }}>{slide.title}</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.tools.map((tool, i) => (
                <div key={i} style={{ background: `${slide.color}10`, padding: '0.75rem', borderRadius: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>{tool.icon}</span>
                    <h4 style={{ color: slide.color, margin: 0, fontSize: '0.85rem' }}>{tool.title}</h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.75rem' }}>
                    {tool.points.map((point, j) => (
                      <li key={j} style={{ color: '#444', marginBottom: '0.15rem' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '0.75rem' }}>
              <h4 style={{ color: '#B45309', marginBottom: '0.75rem' }}>{slide.examples.title}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                {slide.examples.cases.map((cas, i) => (
                  <div key={i} style={{ background: 'white', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.8rem' }}>
                    <strong style={{ color: '#B45309' }}>{cas.situation} :</strong>{' '}
                    <span style={{ color: '#78350F' }}>{cas.action}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'final_conclusion':
        const ConclusionIcon = slide.icon;
        return (
          <div style={{ 
            textAlign: 'center',
            padding: '3rem',
            borderRadius: '1.5rem',
            background: slide.gradient || `linear-gradient(135deg, ${slide.color} 0%, ${slide.color}90 100%)`,
            color: 'white',
            margin: '-1rem'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <ConclusionIcon size={50} style={{ color: 'white' }} />
            </div>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem', color: 'white' }}>{slide.title}</h2>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              {slide.summary.map((point, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.2)',
                  padding: '1rem',
                  borderRadius: '0.75rem',
                  marginBottom: '0.75rem',
                  color: 'white',
                  fontSize: '1.1rem'
                }}>
                  {point}
                </div>
              ))}
            </div>
          </div>
        );

      case 'quiz_activite2':
        if (quizState.completed) {
          const percentage = Math.round((quizState.score / QUIZ_ACTIVITE2.length) * 100);
          return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: percentage >= 70 ? '#D1FAE5' : '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: '2.5rem'
              }}>
                {percentage >= 70 ? '🎉' : '📚'}
              </div>
              <h2 style={{ color: percentage >= 70 ? '#059669' : '#DC2626', fontSize: '2rem', marginBottom: '1rem' }}>
                {percentage >= 70 ? 'Félicitations !' : 'Continuez vos efforts !'}
              </h2>
              <p style={{ fontSize: '1.5rem', color: '#333', marginBottom: '0.5rem' }}>
                Votre score : <strong>{quizState.score}/{QUIZ_ACTIVITE2.length}</strong>
              </p>
              <p style={{ fontSize: '1.25rem', color: '#666', marginBottom: '2rem' }}>
                {percentage}% de bonnes réponses
              </p>
              <button
                onClick={resetQuiz}
                style={{
                  padding: '1rem 2rem',
                  borderRadius: '2rem',
                  border: 'none',
                  background: '#22C55E',
                  color: 'white',
                  fontSize: '1.1rem',
                  cursor: 'pointer'
                }}
              >
                Recommencer le quiz
              </button>
            </div>
          );
        }

        const currentQ = QUIZ_ACTIVITE2[quizState.currentQuestion];
        const getQuestionTypeLabel = () => {
          if (currentQ.type === 'single') return 'QCM - 1 seule réponse';
          if (currentQ.type === 'truefalse') return 'Vrai / Faux';
          return 'QCM - Plusieurs réponses possibles';
        };

        return (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#22C55E', margin: 0, fontSize: '1.5rem' }}>Quiz final - 20 questions</h3>
              <span style={{ 
                background: '#22C55E20', 
                color: '#22C55E', 
                padding: '0.5rem 1rem', 
                borderRadius: '2rem',
                fontWeight: 'bold'
              }}>
                {quizState.currentQuestion + 1}/{QUIZ_ACTIVITE2.length}
              </span>
            </div>
            
            <div style={{ 
              background: currentQ.type === 'multiple' ? '#FEF3C7' : '#F3F4F6', 
              padding: '0.5rem 1rem', 
              borderRadius: '0.5rem', 
              marginBottom: '1rem',
              textAlign: 'center',
              fontSize: '0.9rem',
              color: currentQ.type === 'multiple' ? '#B45309' : '#666'
            }}>
              {getQuestionTypeLabel()}
            </div>

            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '1rem',
              border: '2px solid #e5e7eb',
              marginBottom: '1rem'
            }}>
              <p style={{ fontSize: '1.1rem', fontWeight: '500', marginBottom: '1.5rem', color: '#333' }}>
                {currentQ.question}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {currentQ.options.map((option, idx) => {
                  const isSelected = quizState.selectedAnswers.includes(idx);
                  const isCorrect = currentQ.type === 'multiple' 
                    ? currentQ.correct.includes(idx) 
                    : currentQ.correct === idx;
                  
                  let bgColor = 'white';
                  let borderColor = '#e5e7eb';
                  
                  if (quizState.showResult) {
                    if (isCorrect) {
                      bgColor = '#D1FAE5';
                      borderColor = '#059669';
                    } else if (isSelected && !isCorrect) {
                      bgColor = '#FEE2E2';
                      borderColor = '#DC2626';
                    }
                  } else if (isSelected) {
                    bgColor = '#22C55E20';
                    borderColor = '#22C55E';
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => handleSelectAnswer(idx)}
                      disabled={quizState.showResult}
                      style={{
                        padding: '1rem',
                        borderRadius: '0.75rem',
                        border: `2px solid ${borderColor}`,
                        background: bgColor,
                        cursor: quizState.showResult ? 'default' : 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: currentQ.type === 'multiple' ? '0.25rem' : '50%',
                        background: isSelected ? '#22C55E' : '#f3f4f6',
                        color: isSelected ? 'white' : '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                      }}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span style={{ color: '#333' }}>{option}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {quizState.showResult && (
              <div style={{
                background: '#F0FDF4',
                padding: '1rem',
                borderRadius: '0.75rem',
                marginBottom: '1rem',
                borderLeft: '4px solid #22C55E'
              }}>
                <p style={{ color: '#166534', margin: 0 }}>
                  💡 {currentQ.explanation}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
              {!quizState.showResult ? (
                <button
                  onClick={handleValidateQuizAnswer}
                  disabled={quizState.selectedAnswers.length === 0}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '2rem',
                    border: 'none',
                    background: quizState.selectedAnswers.length === 0 ? '#ccc' : '#22C55E',
                    color: 'white',
                    fontSize: '1rem',
                    cursor: quizState.selectedAnswers.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Valider ma réponse
                </button>
              ) : (
                <button
                  onClick={handleNextQuizQuestion}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '2rem',
                    border: 'none',
                    background: '#22C55E',
                    color: 'white',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  {quizState.currentQuestion < QUIZ_ACTIVITE2.length - 1 ? 'Question suivante' : 'Voir mon score'}
                </button>
              )}
            </div>
          </div>
        );

      default:
        return <div>Contenu à venir...</div>;
    }
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)',
        padding: '1rem 2rem',
        borderRadius: '1rem 1rem 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white'
      }}>
        <span>Activité 2 - Diplôme d'État d'Auxiliaire de Puériculture</span>
        <span>{currentSlide + 1} / {SLIDES.length}</span>
      </div>

      {/* Content */}
      <div style={{
        background: 'white',
        padding: '2rem',
        minHeight: '450px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {renderSlideContent()}
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem',
        padding: '1.5rem',
        background: '#f8f9fa',
        borderRadius: '0 0 1rem 1rem'
      }}>
        <button
          onClick={handlePrevious}
          disabled={currentSlide === 0}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '2rem',
            border: '2px solid #EF4444',
            background: 'white',
            color: '#EF4444',
            cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
            opacity: currentSlide === 0 ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <ChevronLeft size={20} /> Précédent
        </button>

        <button
          onClick={handleSpeak}
          disabled={isPlaying || !slide.narration}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '2rem',
            border: 'none',
            background: '#EF4444',
            color: 'white',
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Volume2 size={20} /> {isPlaying ? 'Lecture...' : 'Écouter'}
        </button>

        <button
          onClick={handleNext}
          disabled={currentSlide === SLIDES.length - 1}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '2rem',
            border: 'none',
            background: '#EF4444',
            color: 'white',
            cursor: currentSlide === SLIDES.length - 1 ? 'not-allowed' : 'pointer',
            opacity: currentSlide === SLIDES.length - 1 ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          Suivant <ChevronRight size={20} />
        </button>
      </div>

      {/* Progress Bar */}
      <div style={{ marginTop: '1rem' }}>
        <div style={{
          height: '6px',
          background: '#e0e0e0',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${((currentSlide + 1) / SLIDES.length) * 100}%`,
            background: 'linear-gradient(90deg, #EF4444 0%, #F87171 100%)',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    </div>
  );
};

export default DEAPActivite2Presentation;
